import { NodeID } from "@hex-insights/app-modules";
import { extractLinks } from "@hex-insights/core";
import { MediaAttachment } from "../../../Utilities";

export * from "./upload";

export function splitAndExtractMediaAttachments<T extends Pick<MediaAttachment, "isFile" | "fileContentType">>(
	mediaAttachments: T[] | null | undefined,
	text: string | null | undefined,
) {
	const visualAttachments: T[] = [];
	const fileAttachments: T[] = [];
	const linkAttachments: (T | ExtractedLinkAttachment)[] = [];

	if (mediaAttachments) {
		for (let i = 0; i < mediaAttachments.length; i++) {
			const attachment = mediaAttachments[i];
			if (attachment.isFile) {
				if (isVisual(attachment)) {
					visualAttachments.push(attachment);
				} else {
					fileAttachments.push(attachment);
				}
			} else {
				linkAttachments.push(attachment);
			}
		}
	}

	if (text) {
		const extractedLinkAttachments = extractLinkAttachments(text);
		linkAttachments.push(...extractedLinkAttachments);
	}

	return { visualAttachments, fileAttachments, linkAttachments };
}

export function splitMediaAttachments<T extends Pick<MediaAttachment, "isFile" | "fileContentType">>(
	mediaAttachments: T[] | null | undefined,
) {
	const visualAttachments: T[] = [];
	const fileAttachments: T[] = [];
	const linkAttachments: T[] = [];

	if (!mediaAttachments) {
		return { visualAttachments, fileAttachments, linkAttachments };
	}

	for (let i = 0; i < mediaAttachments.length; i++) {
		const attachment = mediaAttachments[i];
		if (attachment.isFile) {
			if (isVisual(attachment)) {
				visualAttachments.push(attachment);
			} else {
				fileAttachments.push(attachment);
			}
		} else {
			linkAttachments.push(attachment);
		}
	}

	return { visualAttachments, fileAttachments, linkAttachments };
}

type ExtractedLinkAttachment = {
	id: NodeID;
	title: string;
	linkURL: string;
};

export function extractLinkAttachments(text: string) {
	const links = extractLinks(text);
	const linkAttachments: ExtractedLinkAttachment[] = [];
	const seenURLs = new Set<string>();

	for (let i = 0; i < links.length; i++) {
		const link = links[i];
		if (link.type !== "url") {
			continue;
		}
		if (seenURLs.has(link.href)) {
			continue;
		}
		linkAttachments.push({
			id: `ext-${i}`,
			title: link.value,
			linkURL: link.href,
		});
		seenURLs.add(link.href);
	}

	return linkAttachments;
}

export function countSplitMediaAttachments(
	mediaAttachments: Pick<MediaAttachment, "isFile" | "fileContentType">[] | null | undefined,
) {
	let numImageAttachments = 0;
	let numVideoAttachments = 0;
	let numFileAttachments = 0;
	let numLinkAttachments = 0;

	if (!mediaAttachments) {
		return {
			numImageAttachments,
			numVideoAttachments,
			numFileAttachments,
			numLinkAttachments,
			numVisualAttachments: 0,
		};
	}

	for (let i = 0; i < mediaAttachments.length; i++) {
		const attachment = mediaAttachments[i];
		if (attachment.isFile) {
			if (isImage(attachment)) {
				numImageAttachments++;
			} else if (isVideo(attachment)) {
				numVideoAttachments++;
			} else {
				numFileAttachments++;
			}
		} else {
			numLinkAttachments++;
		}
	}

	return {
		numImageAttachments,
		numVideoAttachments,
		numFileAttachments,
		numLinkAttachments,
		numVisualAttachments: numImageAttachments + numVideoAttachments,
	};
}

export function isVisual(mediaAttachment: Pick<MediaAttachment, "fileContentType">) {
	return isImage(mediaAttachment) || isVideo(mediaAttachment);
}

export function isImage(mediaAttachment: Pick<MediaAttachment, "fileContentType">) {
	return mediaAttachment.fileContentType.startsWith("image/");
}

export function isVideo(mediaAttachment: Pick<MediaAttachment, "fileContentType">) {
	return mediaAttachment.fileContentType.startsWith("video/");
}
